import {
  DeleteOutlined,
  EditOutlined,
  FolderViewOutlined,
} from '@ant-design/icons';
import { LoadingCommon } from '@components';
import { CommonNotifyModal } from '@components/CommonModal/CommonNotifyModal';
import {
  COLOR_TOOLTIP,
  COMMON_ERR_NO_PERMISSION,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  images,
  ZENBU_OPTION,
} from '@constants';
import { masterDataApi } from '@services/masterDataApi';
import { userApi } from '@services/userApi';
import { convertUserListRole, handleShowToastError } from '@utils/helpers';
import {
  COMMON_CONSTANT,
  MODAL_TYPE,
  REGEX_CHARACTER_NUMBER,
  ROLE_DATA,
  USER_ROLE,
} from '@utils/constants';
import { useCurrentUserRole } from '@hooks';
import { Button, Form, Input, Select, TableProps, Tooltip } from 'antd';
import _, { isEmpty, isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { adminPaths } from 'routers/routes';
import { EHttpStatusCode, TAxiosError } from 'types/service';
import {
  TGetCompanyParam,
  TGetStoreParam,
  TRegisUserReq,
  TSearchUserReq,
  TUpdateUserReq,
} from 'types/types';
import CommonUserModal from './components/CommonUserModal';
import { USER_LIST_CONSTANT } from './constants';
import { CommonMasterPage } from '@components/CommonMasterPage/CommonMasterPage';

const { TABLE_KEY, FORM_SEARCH, FORM_MODAL } = USER_LIST_CONSTANT;

// Translation keys reused across multiple places in this file (extracted to
// avoid duplicate-literal warnings and keep a single source of truth).
const USER_PAGE_ID_KEY = 'USER_PAGE.ID';
const USER_PAGE_USERNAME_KEY = 'USER_PAGE.USERNAME';
const USER_PAGE_COMPANY_KEY = 'USER_PAGE.COMPANY';
const USER_PAGE_STORE_KEY = 'USER_PAGE.STORE';
const USER_PAGE_ROLE_KEY = 'USER_PAGE.ROLE';
const COMMON_UPDATE_SUCCESS_TITLE_KEY = 'COMMON.UPDATE_SUCCESS_TITLE';
const COMMON_CONFIRM_TITLE_KEY = 'COMMON.COMMON_CONFIRM';
const FIELD_INVALID_FORMAT_MSG_KEY = 'COMMON_MSG.FIELD_INVALID_FORMAT';
const EXIST_COMPANY_STORE_MSG_KEY = 'COMMON_MSG.EXIST_COMPANY_STORE';
const INPUT_PLACE_HOLDER_KEY = 'COMMON.INPUT_PLACE_HOLDER';
const DROP_DOWN_PLACE_HOLDER_KEY = 'COMMON.DROP_DOWN_PLACE_HOLDER';

// Builds the FIELDS_CONFIG object used by the search form and the modal
// form. Extracted out of the component body to keep UserListPage's own
// cyclomatic complexity down (the "required" flags below are the source of
// several conditional operators).
const getFieldsConfig = (
  t: (key: string) => string,
  modalType: string,
  roleWatch: any
) => ({
  ID: {
    required: false,
    label: t(USER_PAGE_ID_KEY),
    name: FORM_SEARCH.ID,
  },
  USERNAME: {
    required: false,
    label: t(USER_PAGE_USERNAME_KEY),
    name: FORM_SEARCH.NAME,
  },
  COMPANY: {
    required: false,
    label: t(USER_PAGE_COMPANY_KEY),
    name: FORM_SEARCH.COMPANY,
  },
  STORE: {
    required: false,
    label: t(USER_PAGE_STORE_KEY),
    name: FORM_SEARCH.STORE,
  },
  ROLE: {
    required: false,
    label: t(USER_PAGE_ROLE_KEY),
    name: FORM_SEARCH.ROLE,
  },
  ID_MODAL: {
    required: modalType !== MODAL_TYPE.DETAIL,
    label: t(USER_PAGE_ID_KEY),
    name: FORM_MODAL.MODAL_USERNAME,
  },
  USERNAME_MODAL: {
    required: false,
    label: t(USER_PAGE_USERNAME_KEY),
    name: FORM_MODAL.MODAL_NAME,
  },
  PASSWORD_MODAL: {
    required:
      modalType !== MODAL_TYPE.DETAIL &&
      modalType !== MODAL_TYPE.UPDATE &&
      modalType !== MODAL_TYPE.CONFIRM_UPDATE,
    label: t('USER_PAGE.PASSWORD'),
    name: FORM_MODAL.MODAL_PASSWORD,
  },
  COMPANY_MODAL: {
    required:
      modalType !== MODAL_TYPE.DETAIL && roleWatch === USER_ROLE.STORE_OWNER,
    label: t(USER_PAGE_COMPANY_KEY),
    name: FORM_MODAL.MODAL_COMPANY,
  },
  STORE_MODAL: {
    required:
      modalType !== MODAL_TYPE.DETAIL && roleWatch === USER_ROLE.STORE_OWNER,
    label: t(USER_PAGE_STORE_KEY),
    name: FORM_MODAL.MODAL_STORE,
  },
  // No role requires a processing center anymore — vams only has
  // ADMIN/STORE_OWNER, neither of which is scoped to a processing center.
  PROCESSING_CENTER_MODAL: {
    required: false,
    label: t('USER_PAGE.PROCESS_CENTER'),
    name: FORM_MODAL.MODAL_PROCESSING_CENTER,
  },
  ROLE_MODAL: {
    required: modalType !== MODAL_TYPE.DETAIL,
    label: t(USER_PAGE_ROLE_KEY),
    name: FORM_MODAL.MODAL_ROLE,
  },
});

// Computes whether the "Next" button in the user modal should be disabled.
// Extracted out of the component body to keep UserListPage's own cyclomatic
// complexity down and to keep the boolean expression within the allowed
// number of conditional operators.
const computeDisableNext = (
  modalType: string,
  userNameWatch: any,
  passwordWatch: any,
  roleWatch: any,
  companyCodeWatch: any,
  storeCodeWatch: any
): boolean => {
  const isMissingUsername = modalType !== MODAL_TYPE.DETAIL && !userNameWatch;
  const isMissingPasswordForRegistration =
    !passwordWatch && modalType === MODAL_TYPE.REGISTRATION;
  const isStoreOwnerMissingCompanyOrStore =
    roleWatch === USER_ROLE.STORE_OWNER &&
    (!companyCodeWatch || !storeCodeWatch);

  return (
    isMissingUsername ||
    isMissingPasswordForRegistration ||
    !roleWatch ||
    isStoreOwnerMissingCompanyOrStore
  );
};

// The following three predicates back `renderPlaceHolder`. They are kept at
// module scope (rather than as local consts inside the component) so that
// their conditional operators don't count toward UserListPage's own
// cyclomatic complexity, and each one's own expression is split with named
// booleans to stay within the allowed number of conditional operators.
const isRegistrationOrUpdatePlaceholder = (
  modalType: string,
  nameWatch: any,
  passwordWatch: any
) =>
  ((nameWatch || passwordWatch) &&
    modalType.includes(MODAL_TYPE.REGISTRATION)) ||
  modalType.includes(MODAL_TYPE.UPDATE);

const isConfirmRegistrationOrUpdatePlaceholder = (
  modalType: string,
  nameWatch: any,
  passwordWatch: any
) =>
  ((nameWatch || passwordWatch) &&
    modalType.includes(MODAL_TYPE.CONFIRM_REGISTRATION)) ||
  modalType.includes(MODAL_TYPE.CONFIRM_UPDATE);

const isEmptyPlaceholderState = (
  modalType: string,
  nameWatch: any,
  passwordWatch: any
) => {
  const isMissingNameOrPassword = !nameWatch || !passwordWatch;
  const isConfirmRegistrationMissingInfo =
    isMissingNameOrPassword &&
    modalType.includes(MODAL_TYPE.CONFIRM_REGISTRATION);
  const isConfirmUpdateType = modalType.includes(MODAL_TYPE.CONFIRM_UPDATE);
  const isDetailType = modalType.includes(MODAL_TYPE.DETAIL);

  return (
    isConfirmRegistrationMissingInfo || isConfirmUpdateType || isDetailType
  );
};

// Search inputs should be trimmed and matched case-insensitively (handled
// server-side via ILIKE); trimming here keeps leading/trailing spaces from
// being sent as part of the search filter.
const trimSearchValue = (value: any) =>
  typeof value === 'string' ? value.trim() : value;

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const GUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Some records come back with a placeholder GUID in the name field instead
// of a real name (e.g. an unassigned company/store default). Never surface
// a raw GUID in the UI — show blank instead.
const displayNameOrBlank = (value: any) =>
  typeof value === 'string' && value && !GUID_REGEX.test(value) ? value : '';

const UserListPage = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formModal] = Form.useForm();
  const currentUserRole = useCurrentUserRole();
  const currentUserName = localStorage.getItem('USERNAME');

  const userNameWatch = Form.useWatch(FORM_MODAL.MODAL_USERNAME, formModal);
  const nameWatch = Form.useWatch(FORM_MODAL.MODAL_NAME, formModal);
  const passwordWatch = Form.useWatch(FORM_MODAL.MODAL_PASSWORD, formModal);
  const roleWatch = Form.useWatch(FORM_MODAL.MODAL_ROLE, formModal);
  const companyCodeWatch = Form.useWatch(FORM_MODAL.MODAL_COMPANY, formModal);
  const storeCodeWatch = Form.useWatch(FORM_MODAL.MODAL_STORE, formModal);
  const processingCodeWatch = Form.useWatch(
    FORM_MODAL.MODAL_PROCESSING_CENTER,
    formModal
  );

  const [dataSource, setDataSource] = useState<any>([]);
  const [storeModal, setStoreModal] = useState<any>([]);
  const [companyModal, setCompanyModal] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [isLoadingModal, setIsLoadingModal] = useState<boolean>(false);
  const [isDisableStore, setIsDisableStore] = useState<boolean>(false);
  const [companyDropdownData, setCompanyDropdownData] = useState<any>([]);

  const [isDisableCompany, setIsDisableCompany] = useState<boolean>(false);
  const [isSuccessResponse, setIsSuccessResponse] = useState<boolean>(false);
  const [storeCodeDropdownData, setStoreCodeDropdownData] = useState<any>([]);

  const [currentPage, setCurrentPage] = useState(1); // Track current page
  const [currentTableRecord, setCurrentTableRecord] = useState<any>(); // Current record of table

  const [isStoreSuccessResponse, setIsStoreSuccessResponse] =
    useState<boolean>(true);
  const [isDisableCompanyModal, setIsDisableCompanyModal] =
    useState<boolean>(false);
  const [isDisableStoreModal, setIsDisableStoreModal] =
    useState<boolean>(false);
  const [isDisableProcessCenterModal, setIsDisableProcessCenterModal] =
    useState<boolean>(false);
  // const [processCenterDropdownData, setProcessCenterDropdownData] =
  //   useState<any>([]);
  const [commonUserModal, setCommonUserModal] = useState<{
    visible: boolean;
    modalType: string;
    title: string;
    image?: string;
    message?: string;
    disableField: boolean;
  }>({
    visible: false,
    modalType: '',
    title: '',
    disableField: false,
  });
  const [commonNotifyModal, setCommonNotifyModal] = useState<{
    visible: boolean;
    modalType: string;
    title: string;
    image?: string;
    message?: string;
    userIdRecord: string;
    userIdName: string;
  }>({
    visible: false,
    modalType: '',
    title: '',
    image: '',
    message: '',
    userIdRecord: '',
    userIdName: '',
  });

  const FIELDS_CONFIG = getFieldsConfig(
    t,
    commonUserModal.modalType,
    roleWatch
  );

  const disableNext = computeDisableNext(
    commonUserModal.modalType,
    userNameWatch,
    passwordWatch,
    roleWatch,
    companyCodeWatch,
    storeCodeWatch
  );

  const handleGetCompanies = async (payload: TGetCompanyParam) => {
    try {
      const response = await masterDataApi.getCompany(payload);
      if (response.ok) {
        const companyData = response?.data?.map((val) => ({
          value: val.id,
          label: val.companyCode + '_' + val.companyName,
        }));
        setCompanyDropdownData([ZENBU_OPTION, ...companyData]);
        setCompanyModal(companyData);
        form.setFieldValue(FORM_SEARCH.COMPANY, ZENBU_OPTION.value);
        await handleGetStoreByCompany(
          {
            companyId: form.getFieldValue(FORM_SEARCH.COMPANY),
          },
          false,
          true
        );
        // await handleGetProcessingCenter({
        //   companyId: "",
        // });
        setIsSuccessResponse(true);
      }
    } catch (error) {
      handleShowToastError(error, '');
      setIsLoading(false);
    }
  };

  const handleGetStoreByCompany = async (
    payload: TGetStoreParam,
    isModal: boolean,
    isInit: boolean
  ) => {
    try {
      const response = await masterDataApi.getStore(payload);
      if (!response.ok) {
        return;
      }
      const storeData =
        response?.data?.map((val) => ({
          value: val.id,
          label: val.storeCode + '_' + val.storeName,
        })) ?? [];
      if (!isModal && isInit) {
        setStoreCodeDropdownData([ZENBU_OPTION, ...storeData]);
        form.setFieldValue(
          FORM_SEARCH.STORE,
          storeData[0]?.value || ZENBU_OPTION.value
        );
      }
      if (!isModal) {
        if (storeData && storeData.length === 0) {
          form.resetFields([FORM_SEARCH.STORE]);
          setStoreCodeDropdownData([]);
          return;
        }
        setStoreCodeDropdownData([ZENBU_OPTION, ...storeData]);
      } else {
        setStoreModal(storeData);
      }
      setIsStoreSuccessResponse(true);
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  // const handleGetProcessingCenter = async (payload: TGetStoreParam) => {
  //   try {
  //     const response = await masterDataApi.getProcessingCenter(payload);
  //     if (response.ok) {
  //       const processingCenterData = response?.data?.map((val) => ({
  //         value: val.id,
  //         label: val.processingCenterCode + '_' + val.processingCenterName,
  //       }));
  //       setProcessCenterDropdownData(processingCenterData);
  //     }
  //   } catch (error) {
  //     handleShowToastError(error, '');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleSearchUser = async (payload: TSearchUserReq) => {
    setIsLoading(true);
    setDataSource([]);
    try {
      const response: any = await userApi.searchUserList(payload);
      if (response.ok) {
        const searchResults = response?.data?.map(
          (item: any, index: number) => {
            return {
              no: index + 1,
              ...item,
            };
          }
        );
        setCurrentTableRecord(response?.pagination?.totalCount);
        setDataSource(searchResults);
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchUserListScroll = async (payload: TSearchUserReq) => {
    setIsLoading(true);
    try {
      const response: any = await userApi.searchUserList(payload);
      if (response.ok) {
        const searchResults = response?.data;
        const newDataSource = [...dataSource, ...searchResults].map(
          (item, index: number) => {
            return {
              no: index + 1,
              ...item,
            };
          }
        );
        setDataSource(newDataSource);
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetUserDetail = async (payload: string) => {
    setIsLoadingModal(true);
    try {
      const response = await userApi.getUserDetail(payload);
      if (response.ok) {
        const detailData = response?.data;
        const hasCompany =
          !isNil(detailData.companyId) && detailData.companyId !== EMPTY_GUID;
        const hasStore =
          !isNil(detailData.storeId) && detailData.storeId !== EMPTY_GUID;
        setCurrentUserId(detailData.id);
        formModal.setFieldsValue({
          [FORM_MODAL.MODAL_USERNAME]: detailData.userName,
          [FORM_MODAL.MODAL_NAME]: !isNil(detailData.fullName)
            ? detailData.fullName
            : '',
          [FORM_MODAL.MODAL_PASSWORD]: '',
          [FORM_MODAL.MODAL_COMPANY]: hasCompany ? detailData.companyId : '',
          [FORM_MODAL.MODAL_PROCESSING_CENTER]: !isNil(
            detailData.processingCenterId
          )
            ? detailData.processingCenterId
            : '',
          [FORM_MODAL.MODAL_ROLE]: detailData.roleCode,
        });
        formModal.setFieldValue(
          FORM_MODAL.MODAL_STORE,
          hasStore ? detailData.storeId : ''
        );
        handleGetStoreByCompany(
          {
            companyId: detailData.companyId,
          },
          true,
          false
        );
      }
    } catch (error) {
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
      setIsLoadingModal(false);
    }
  };
  const handleRegisUser = async (payload: TRegisUserReq) => {
    try {
      const response = await userApi.regisUser(payload);
      if (response.ok) {
        setCommonUserModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_REGISTRATION,
          title: t(COMMON_UPDATE_SUCCESS_TITLE_KEY),
          image: images.confirmApproveTemperature,
          message: t('COMMON_MSG.SUCCESS_REGISTER'),
          userIdRecord: '',
          userIdName: '',
        });
        handleSearchUser({
          username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
          fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
          roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
          storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
        });
      }
    } catch (error: any) {
      const err = error as TAxiosError;
      const errorCode = err?.data?.code;
      setCommonUserModal({
        visible: false,
        modalType: '',
        title: '',
        disableField: false,
      });
      setCommonNotifyModal({
        visible: true,
        modalType: MODAL_TYPE.FAIL_REGISTRATION,
        title: t(COMMON_CONFIRM_TITLE_KEY),
        image: images.importDataFail,
        message:
          errorCode === COMMON_CONSTANT.VALIDATE_USER.USER_ALREADY_EXISTED
            ? t('COMMON_MSG.DATA_EXIST')
            : t('COMMON_MSG.FAIL_REGISTER'),
        userIdRecord: '',
        userIdName: '',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (payload: TUpdateUserReq) => {
    try {
      const response = await userApi.updateUser(payload);
      if (response.ok) {
        setCommonUserModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_UPDATE,
          title: t(COMMON_UPDATE_SUCCESS_TITLE_KEY),
          image: images.confirmApproveTemperature,
          message: t('COMMON_MSG.SUCCESS_UPDATE'),
          userIdRecord: '',
          userIdName: '',
        });
        handleSearchUser({
          username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
          fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
          roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
          storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
        });
      }
    } catch (error: any) {
      setCommonUserModal({
        visible: false,
        modalType: '',
        title: '',
        disableField: false,
      });
      setCommonNotifyModal({
        visible: true,
        modalType: MODAL_TYPE.FAIL_UPDATE,
        title: t(COMMON_CONFIRM_TITLE_KEY),
        image: images.importDataFail,
        message: t('COMMON_MSG.UPDATE_FAIL'),
        userIdRecord: '',
        userIdName: '',
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteUser = async (payload: string) => {
    try {
      setIsLoading(true);
      const response = await userApi.deleteUser(payload);
      if (response.ok) {
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.SUCCESS_DELETE,
          title: t(COMMON_UPDATE_SUCCESS_TITLE_KEY),
          image: images.confirmApproveTemperature,
          message: t('USER_PAGE.DELETE_SUCCESS'),
          userIdRecord: '',
          userIdName: '',
        });
        handleSearchUser({
          username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
          fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
          roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
          companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
          storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
          page: DEFAULT_PAGE_NUMBER,
          pageSize: DEFAULT_PAGE_SIZE,
        });
      }
    } catch (error) {
      const err = error as TAxiosError;
      const errorCode = err?.data?.code;
      setCommonNotifyModal({
        visible: true,
        modalType: MODAL_TYPE.FAIL_DELETE,
        title: t(COMMON_CONFIRM_TITLE_KEY),
        image: images.importDataFail,
        message:
          errorCode === COMMON_CONSTANT.VALIDATE_USER.NOT_FOUND_USER
            ? t('COMMON_MSG.NOT_FOUND_STORE')
            : t('COMMON_MSG.FAIL_DELETE'),
        userIdRecord: '',
        userIdName: '',
      });
      handleShowToastError(error, '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldValue(FORM_SEARCH.ROLE, ZENBU_OPTION.value);
    handleGetCompanies({
      storeId: '',
    });
  }, []);

  useEffect(() => {
    if (isSuccessResponse && isStoreSuccessResponse) {
      handleSearchUser({
        username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
        fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
        roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
        companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
        storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
        page: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    }
  }, [isSuccessResponse, isStoreSuccessResponse]);

  const handleSearch = () => {
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    handleSearchUser({
      username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
      fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
      roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
      companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
      storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
      page: DEFAULT_PAGE_NUMBER,
      pageSize: DEFAULT_PAGE_SIZE,
    });
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollTop + clientHeight >= scrollHeight - 10 &&
      !isLoading &&
      dataSource.length < currentTableRecord
    ) {
      setCurrentPage((prev) => prev + 1);
      handleSearchUserListScroll({
        username: trimSearchValue(form.getFieldValue(FORM_SEARCH.ID)),
        fullName: trimSearchValue(form.getFieldValue(FORM_SEARCH.NAME)),
        roleCode: form.getFieldValue(FORM_SEARCH.ROLE),
        companyId: form.getFieldValue(FORM_SEARCH.COMPANY) ?? '',
        storeId: form.getFieldValue(FORM_SEARCH.STORE) ?? '',
        page: currentPage + 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
    }
  };

  const handleOpenModalDetail = (record: any) => {
    handleGetStoreByCompany(
      {
        companyId: record.companyId,
      },
      true,
      false
    );
    handleGetUserDetail(record.id);
    setCommonUserModal({
      visible: true,
      modalType: MODAL_TYPE.DETAIL,
      title: t('USER_PAGE.DETAIL_USER'),
      disableField: true,
    });
  };

  const handleOpenModalUpdate = (record: any) => {
    if (record && record.roleCode === USER_ROLE.ADMIN) {
      setIsDisableCompanyModal(true);
      setIsDisableStoreModal(true);
      setIsDisableProcessCenterModal(true);
    }
    if (record && record.roleCode === USER_ROLE.STORE_OWNER) {
      setIsDisableProcessCenterModal(true);
      setIsDisableCompanyModal(false);
      setIsDisableStoreModal(false);
    }
    handleGetUserDetail(record.id);
    setCommonUserModal({
      visible: true,
      modalType: MODAL_TYPE.UPDATE,
      title: t('USER_PAGE.UPDATE_USER'),
      disableField: false,
    });
  };

  const handleOpenModalDelete = (record: any) => {
    setCommonNotifyModal({
      visible: true,
      modalType: MODAL_TYPE.DELETE,
      title: t('COMMON.DELETE'),
      image: images.commonDelete,
      message: t('COMMON_MSG.DELETE_USER_CONFIRM_MSG'),
      userIdRecord: record?.id,
      userIdName: record?.userName,
    });
  };

  const handleRedirectUser = () => {
    handleShowToastError(COMMON_ERR_NO_PERMISSION, t('USER_PAGE.PAGE_TITLE'));
    return <Navigate to={adminPaths.fixedAssetLedger} />;
  };

  const handleOpenRegisModal = () => {
    setIsDisableCompanyModal(true);
    setIsDisableStoreModal(true);
    setIsDisableProcessCenterModal(true);
    formModal.resetFields([FORM_MODAL.MODAL_COMPANY]);
    formModal.resetFields([FORM_MODAL.MODAL_STORE]);
    formModal.resetFields([FORM_MODAL.MODAL_PROCESSING_CENTER]);
    setCommonUserModal({
      visible: true,
      modalType: MODAL_TYPE.REGISTRATION,
      title: t('USER_PAGE.REGIS_USER'),
      disableField: false,
    });
  };

  const handleOpenModalConfirmFinal = () => {
    if (commonUserModal.modalType === MODAL_TYPE.CONFIRM_REGISTRATION) {
      setCommonNotifyModal({
        ...commonUserModal,
        modalType: MODAL_TYPE.CONFIRM_REGISTRATION_FINAL,
        title: t('USER_PAGE.CONFIRM'),
        image: images.confirmImage,
        message: t('COMMON_MSG.CONFIRM_REGIS'),
        userIdRecord: '',
        userIdName: '',
      });
    }
    if (commonUserModal.modalType === MODAL_TYPE.CONFIRM_UPDATE) {
      setCommonNotifyModal({
        ...commonUserModal,
        modalType: MODAL_TYPE.CONFIRM_UPDATE_FINAL,
        title: t('USER_PAGE.CONFIRM'),
        image: images.confirmImage,
        message: t('COMMON_MSG.CONFIRM_UPDATE'),
        userIdRecord: '',
        userIdName: '',
      });
    }
  };

  const handleFormResetFieldError = () => {
    formModal.setFields([
      {
        name: [FORM_MODAL.MODAL_USERNAME],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_NAME],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_PASSWORD],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_STORE],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_COMPANY],
        errors: [],
      },
      {
        name: [FORM_MODAL.MODAL_ROLE],
        errors: [],
      },
    ]);
  };

  const handleValidateForm = () => {
    handleFormResetFieldError();
    let checkError = false;
    const validateField = (condition: boolean, fieldName: string) => {
      if (condition) {
        formModal.setFields([
          {
            name: [fieldName],
            errors: [t(FIELD_INVALID_FORMAT_MSG_KEY)],
          },
        ]);
        checkError = true;
      }
    };

    validateField(
      userNameWatch && userNameWatch.toString().length < 6,
      FORM_MODAL.MODAL_USERNAME
    );
    validateField(
      nameWatch && nameWatch.toString().length > 255,
      FORM_MODAL.MODAL_NAME
    );
    validateField(
      (passwordWatch && passwordWatch.toString().length < 8) ||
        !REGEX_CHARACTER_NUMBER.test(passwordWatch),
      FORM_MODAL.MODAL_PASSWORD
    );
    validateField(
      roleWatch === USER_ROLE.STORE_OWNER && !companyCodeWatch,
      FORM_MODAL.MODAL_COMPANY
    );
    validateField(
      roleWatch === USER_ROLE.STORE_OWNER && !storeCodeWatch,
      FORM_MODAL.MODAL_STORE
    );

    if (!checkError) {
      formModal.setFields([
        {
          name: [FORM_MODAL.MODAL_USERNAME],
          errors: [],
          value: formModal?.getFieldsValue()?.usernameModal?.trim(),
        },
        {
          name: [FORM_MODAL.MODAL_NAME],
          errors: [],
          value: formModal?.getFieldsValue()?.nameModal?.trim(),
        },
        {
          name: [FORM_MODAL.MODAL_PASSWORD],
          errors: [],
          value: formModal?.getFieldsValue()?.passwordModal?.trim(),
        },
        { name: [FORM_MODAL.MODAL_COMPANY], errors: [] },
        { name: [FORM_MODAL.MODAL_STORE], errors: [] },
      ]);

      const params: TRegisUserReq = {
        fullName: nameWatch,
        userName: userNameWatch,
        password: passwordWatch,
        companyId: companyCodeWatch,
        storeId: storeCodeWatch,
        processingCenterId: processingCodeWatch,
        roleCode: roleWatch,
        isValidationOnly: true,
      };
      if (commonUserModal.modalType === MODAL_TYPE.REGISTRATION) {
        handleValidateUser(params);
      }

      if (commonUserModal.modalType === MODAL_TYPE.UPDATE) {
        handleValidateUserUpdate({ ...params, id: currentUserId });
      }
    }
  };

  const handleErrorCode = (errorCode: string) => {
    switch (errorCode) {
      case COMMON_CONSTANT.VALIDATE_USER.USER_ALREADY_EXISTED:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_USERNAME],
            errors: [t('COMMON_MSG.DATA_EXIST')],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.STORE_MANAGER_ALREADY_EXISTED:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_COMPANY],
            errors: [t(EXIST_COMPANY_STORE_MSG_KEY)],
          },
          {
            name: [FORM_MODAL.MODAL_STORE],
            errors: [t(EXIST_COMPANY_STORE_MSG_KEY)],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.INVALID_PASSWORD_FORMAT:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_PASSWORD],
            errors: [t(FIELD_INVALID_FORMAT_MSG_KEY)],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.STORE_NOT_EXISTED:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_STORE],
            errors: [t(EXIST_COMPANY_STORE_MSG_KEY)],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.INVALID_NAME:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_NAME],
            errors: [t(FIELD_INVALID_FORMAT_MSG_KEY)],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.INVALID_USERNAME_FORMAT:
        formModal.setFields([
          {
            name: [FORM_MODAL.MODAL_USERNAME],
            errors: [t(FIELD_INVALID_FORMAT_MSG_KEY)],
          },
        ]);
        break;
      case COMMON_CONSTANT.VALIDATE_USER.USER_NOT_EXSITED:
        setCommonUserModal({
          visible: false,
          modalType: '',
          title: '',
          disableField: false,
        });
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_DELETE,
          title: t(COMMON_CONFIRM_TITLE_KEY),
          image: images.importDataFail,
          message: t('COMMON_MSG.DATA_NOT_EXIST'),
          userIdRecord: '',
          userIdName: '',
        });
        break;
      default:
        break;
    }
  };

  const handleValidateUser = async (payload: TRegisUserReq) => {
    try {
      const response = await userApi.regisUser(payload);
      if (response.ok) {
        setCommonUserModal({
          visible: true,
          modalType: MODAL_TYPE.CONFIRM_REGISTRATION,
          disableField: true,
          title: t('USER_PAGE.REGIS_USER'),
        });
      }
    } catch (error) {
      const err = error as TAxiosError;
      if (
        err.status === EHttpStatusCode.BAD_REQUEST &&
        err?.data?.errors.length
      ) {
        const result = _.uniq(
          err.data.errors.map((item: any) => item.code?.toString())
        );
        if (!result.length) {
          return;
        } else {
          const errorCode: any = result[0];
          handleErrorCode(errorCode);
        }
        return;
      }
      handleShowToastError(error, '');
    }
  };

  const handleValidateUserUpdate = async (payload: TUpdateUserReq) => {
    try {
      const response = await userApi.updateUser(payload);
      if (response.ok) {
        setCommonUserModal({
          visible: true,
          modalType: MODAL_TYPE.CONFIRM_UPDATE,
          disableField: true,
          title: t('USER_PAGE.UPDATE_USER'),
        });
      }
    } catch (error) {
      const err = error as TAxiosError;
      if (
        err.status === EHttpStatusCode.BAD_REQUEST &&
        err?.data?.errors.length
      ) {
        const result = _.uniq(
          err.data.errors.map((item: any) => item.code?.toString())
        );
        if (!result.length) {
          return;
        } else {
          const errorCode: any = result[0];
          handleErrorCode(errorCode);
        }
        return;
      }
      handleShowToastError(error, '');
    }
  };

  const handleFinalAction = () => {
    const params: TRegisUserReq = {
      fullName: nameWatch,
      userName: userNameWatch,
      password: passwordWatch,
      companyId: companyCodeWatch,
      storeId: storeCodeWatch,
      processingCenterId: processingCodeWatch,
      roleCode: roleWatch,
      isValidationOnly: false,
    };
    if (commonNotifyModal.modalType === MODAL_TYPE.CONFIRM_REGISTRATION_FINAL) {
      handleRegisUser(params);
    }
    if (commonNotifyModal.modalType === MODAL_TYPE.CONFIRM_UPDATE_FINAL) {
      handleUpdateUser({
        ...params,
        id: currentUserId,
      });
    }
    if (commonNotifyModal.modalType === MODAL_TYPE.DELETE) {
      if (currentUserName === commonNotifyModal?.userIdName) {
        setCommonNotifyModal({
          visible: true,
          modalType: MODAL_TYPE.FAIL_DELETE,
          title: t(COMMON_CONFIRM_TITLE_KEY),
          image: images.importDataFail,
          message: t('COMMON_MSG.SELF_DELETE_ACCOUNT'),
          userIdRecord: '',
          userIdName: '',
        });
      } else {
        handleDeleteUser(commonNotifyModal?.userIdRecord);
      }
    }
  };

  const handleChangeCompany = (value: any) => {
    if (isEmpty(value) && value != '') {
      form.setFieldValue(FORM_SEARCH.STORE, '');
    }
    if (value || value == '') {
      setIsDisableStore(false);
      form.resetFields([FORM_SEARCH.STORE]);
      handleGetStoreByCompany(
        {
          companyId: value,
        },
        false,
        false
      );
    }
  };

  const handleChangeRole = (value: any) => {
    if (isEmpty(value) && value != '') {
      setIsDisableCompany(false);
      setIsDisableStore(true);
      form.setFieldsValue({
        [FORM_SEARCH.COMPANY]: '',
        [FORM_SEARCH.STORE]: '',
      });
    } else {
      setIsDisableStore(false);
      setIsDisableCompany(false);
    }

    if (value == USER_ROLE.STORE_OWNER) {
      setIsDisableCompany(false);
      setIsDisableStore(false);
      form.setFieldValue(FORM_SEARCH.COMPANY, companyDropdownData?.[1].value);
      handleGetStoreByCompany(
        {
          companyId: companyDropdownData?.[1].value,
        },
        false,
        false
      );
    }

    if (!isEmpty(value) && value !== USER_ROLE.STORE_OWNER) {
      setIsDisableCompany(false);
      setIsDisableStore(false);
      form.setFieldsValue({
        [FORM_SEARCH.COMPANY]: '',
        [FORM_SEARCH.STORE]: '',
      });
    }
  };

  const handleChangeRoleModal = (value: string) => {
    setStoreModal([]);
    formModal.setFieldsValue({
      [FORM_MODAL.MODAL_COMPANY]: undefined,
      [FORM_MODAL.MODAL_STORE]: undefined,
      [FORM_MODAL.MODAL_PROCESSING_CENTER]: undefined,
    });

    if (value == USER_ROLE.STORE_OWNER) {
      setIsDisableProcessCenterModal(true);
      setIsDisableCompanyModal(false);
      setIsDisableStoreModal(false);
      return;
    }

    // USER_ROLE.ADMIN (or anything else) needs no company/store/processing
    // center scope.
    setIsDisableStoreModal(true);
    setIsDisableCompanyModal(true);
    setIsDisableProcessCenterModal(true);
  };

  const handleChangeCompanyModal = (value: string) => {
    if (value) {
      formModal.resetFields([FORM_MODAL.MODAL_STORE]);
      handleGetStoreByCompany(
        {
          companyId: value,
        },
        true,
        false
      );
    }
  };

  const columns: TableProps<any>['columns'] = [
    {
      title: t('USER_PAGE.NO'),
      key: TABLE_KEY.NO,
      dataIndex: TABLE_KEY.NO,
      align: 'center',
      width: 50,
      render(text) {
        return <div className="cell-data">{text}</div>;
      },
    },
    {
      title: t(USER_PAGE_ID_KEY),
      key: TABLE_KEY.ID,
      dataIndex: TABLE_KEY.ID,
      align: 'center',
      width: 150,
      render(text, _record: any) {
        return <div className="cell-data">{text}</div>;
      },
    },
    {
      title: t(USER_PAGE_USERNAME_KEY),
      key: TABLE_KEY.NAME,
      dataIndex: TABLE_KEY.NAME,
      align: 'center',
      width: 150,
      render(text, _record: any) {
        return (
          <div className="list-page_ellipsis">
            <Tooltip
              color="white"
              overlayClassName="common_custom-tooltip"
              title={text}
              overlayInnerStyle={{
                color: COLOR_TOOLTIP,
                whiteSpace: 'pre-wrap',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t(USER_PAGE_ROLE_KEY),
      key: TABLE_KEY.ROLE,
      dataIndex: TABLE_KEY.ROLE,
      align: 'center',
      width: 150,
      render(text, record: any) {
        return (
          <div className="list-page_ellipsis">
            <Tooltip
              color="white"
              overlayClassName="common_custom-tooltip"
              title={convertUserListRole(record?.roleCode)}
              overlayInnerStyle={{
                color: COLOR_TOOLTIP,
              }}
            >
              {convertUserListRole(record?.roleCode)}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t(USER_PAGE_COMPANY_KEY),
      key: TABLE_KEY.COMPANY_CODE,
      dataIndex: TABLE_KEY.COMPANY_CODE,
      align: 'center',
      width: 180,
      render(_text, record: any) {
        const companyInfo = isEmpty(record.companyCode)
          ? ''
          : displayNameOrBlank(`${record.companyCode}_${record.companyName}`);
        return (
          <div className="list-page_ellipsis">
            <Tooltip
              color="white"
              overlayClassName="common_custom-tooltip"
              title={companyInfo}
              overlayInnerStyle={{
                color: COLOR_TOOLTIP,
              }}
            >
              {companyInfo}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t(USER_PAGE_STORE_KEY),
      key: TABLE_KEY.STORE_CODE,
      dataIndex: TABLE_KEY.STORE_CODE,
      align: 'center',
      width: 150,
      render(_text, record: any) {
        const storeInfo = isEmpty(record.storeCode)
          ? ''
          : displayNameOrBlank(`${record.storeCode}_${record.storeName}`);
        return (
          <div className="list-page_ellipsis">
            <Tooltip
              color="white"
              overlayClassName="common_custom-tooltip"
              title={storeInfo}
              overlayInnerStyle={{
                color: COLOR_TOOLTIP,
              }}
            >
              {storeInfo}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('COMMON.ACTION'),
      align: 'center',
      width: 144,
      render(_, record: any) {
        return (
          <div className="list-page_modal-btns">
            <Button
              onClick={() => {
                handleOpenModalDetail(record);
              }}
            >
              <FolderViewOutlined
                style={{
                  fontSize: '21px',
                  color: '#344054',
                  cursor: 'pointer',
                }}
              />
            </Button>
            <Button
              disabled={currentUserRole === USER_ROLE.STORE_OWNER}
              onClick={() => handleOpenModalUpdate(record)}
            >
              <EditOutlined
                style={{
                  fontSize: '21px',
                  color: `${
                    currentUserRole === USER_ROLE.STORE_OWNER
                      ? '#99a6bb'
                      : '#344054'
                  }`,
                  cursor: 'pointer',
                }}
              />
            </Button>
            <Button
              disabled={currentUserRole === USER_ROLE.STORE_OWNER}
              onClick={() => {
                handleOpenModalDelete(record);
              }}
            >
              <DeleteOutlined
                style={{
                  fontSize: '21px',
                  color: `${
                    currentUserRole === USER_ROLE.STORE_OWNER
                      ? '#99a6bb'
                      : '#F04438'
                  }`,
                  cursor: 'pointer',
                }}
              />
            </Button>
          </div>
        );
      },
    },
  ];

  // Clear search
  let previousCompanyId = form.getFieldValue(FORM_SEARCH.COMPANY);
  const handleClearSearch = () => {
    form.resetFields();
    form.setFieldValue(FORM_SEARCH.ROLE, '');
    form.setFieldValue(FORM_SEARCH.COMPANY, '');
    form.setFieldValue(FORM_SEARCH.STORE, '');
    setIsDisableCompany(false);
    setIsDisableStore(false);
    const currentCompanyId = form.getFieldValue(FORM_SEARCH.COMPANY);
    if (currentCompanyId !== previousCompanyId) {
      handleGetStoreByCompany(
        {
          companyId: currentCompanyId,
        },
        false,
        false
      );
      previousCompanyId = currentCompanyId;
    }
  };

  const renderPlaceHolder = () => {
    let renderPlaceHolderText = t(INPUT_PLACE_HOLDER_KEY);

    if (
      isRegistrationOrUpdatePlaceholder(
        commonUserModal.modalType,
        nameWatch,
        passwordWatch
      )
    ) {
      renderPlaceHolderText = t(INPUT_PLACE_HOLDER_KEY);
    }

    if (
      isConfirmRegistrationOrUpdatePlaceholder(
        commonUserModal.modalType,
        nameWatch,
        passwordWatch
      )
    ) {
      renderPlaceHolderText = t(INPUT_PLACE_HOLDER_KEY);
    }

    if (
      isEmptyPlaceholderState(
        commonUserModal.modalType,
        nameWatch,
        passwordWatch
      )
    ) {
      renderPlaceHolderText = '';
    }

    return renderPlaceHolderText;
  };

  return (
    <>
      {currentUserRole === USER_ROLE.STORE_OWNER ? (
        handleRedirectUser()
      ) : (
        <LoadingCommon loading={isLoading}>
          <CommonMasterPage
            handleClearSearch={handleClearSearch}
            handleSearch={handleSearch}
            handleScroll={handleScroll}
            handleOpenRegisModal={handleOpenRegisModal}
            pageName={'USER_PAGE'}
            commonName="COMMON"
            form={form}
            columns={columns}
            listData={dataSource}
            rowKey={dataSource ?? dataSource?.map((item: any) => item.id)}
            listItems={[
              {
                fieldConfig: FIELDS_CONFIG.ID,
                element: (
                  <Input size="large" placeholder={t(INPUT_PLACE_HOLDER_KEY)} />
                ),
              },
              {
                fieldConfig: FIELDS_CONFIG.USERNAME,
                element: (
                  <Input size="large" placeholder={t(INPUT_PLACE_HOLDER_KEY)} />
                ),
              },
              {
                fieldConfig: FIELDS_CONFIG.ROLE,
                element: (
                  <Select
                    size="large"
                    placeholder={t(DROP_DOWN_PLACE_HOLDER_KEY)}
                    options={[ZENBU_OPTION, ...ROLE_DATA]}
                    onChange={handleChangeRole}
                  />
                ),
              },
              {
                fieldConfig: FIELDS_CONFIG.COMPANY,
                element: (
                  <Select
                    size="large"
                    placeholder={t(DROP_DOWN_PLACE_HOLDER_KEY)}
                    options={companyDropdownData}
                    disabled={isDisableCompany}
                    onChange={handleChangeCompany}
                  />
                ),
              },
              {
                fieldConfig: FIELDS_CONFIG.STORE,
                element: (
                  <Select
                    showSearch
                    size="large"
                    optionFilterProp="label"
                    placeholder={t(DROP_DOWN_PLACE_HOLDER_KEY)}
                    options={storeCodeDropdownData}
                    disabled={isDisableStore}
                  />
                ),
              },
            ]}
          />
          {commonNotifyModal.visible && (
            <CommonNotifyModal
              confirmLoading={isLoading}
              width={416}
              isCloseIcon={false}
              isMaskClosable={false}
              title={commonNotifyModal.title}
              image={commonNotifyModal.image}
              visible={commonNotifyModal.visible}
              message={commonNotifyModal.message}
              modalType={commonNotifyModal.modalType}
              userNameRecord={commonNotifyModal.userIdName}
              handleFinalAction={handleFinalAction}
              onClose={() => {
                setCommonNotifyModal({
                  visible: false,
                  modalType: '',
                  title: '',
                  userIdRecord: '',
                  userIdName: '',
                });
              }}
            ></CommonNotifyModal>
          )}
          <CommonUserModal
            isDisableProcessCenterModal={isDisableProcessCenterModal}
            setCommonUserModal={setCommonUserModal}
            setIsDisableCompanyModal={setIsDisableCompanyModal}
            setIsDisableProcessCenterModal={setIsDisableProcessCenterModal}
            setIsDisableStoreModal={setIsDisableStoreModal}
            translate={t}
            FORM_MODAL={FORM_MODAL}
            disableNext={disableNext}
            formModal={formModal}
            handleFinalAction={handleFinalAction}
            handleOpenModalConfirmFinal={handleOpenModalConfirmFinal}
            handleValidateForm={handleValidateForm}
            FIELDS_CONFIG={FIELDS_CONFIG}
            commonUserModal={commonUserModal}
            isLoadingModal={isLoadingModal}
            // processCenterDropdownData={processCenterDropdownData}
            handleChangeRoleModal={handleChangeRoleModal}
            renderPlaceHolder={renderPlaceHolder}
            isDisableStoreModal={isDisableStoreModal}
            isDisableCompanyModal={isDisableCompanyModal}
            handleChangeCompanyModal={handleChangeCompanyModal}
            companyModal={companyModal}
            storeModal={storeModal}
          />
        </LoadingCommon>
      )}
    </>
  );
};

export { UserListPage };
